/**
 * KST 포맷터는 모듈에서 한 번만 만든다.
 * 옵션을 넘긴 `toLocaleString` 은 호출마다 `Intl.DateTimeFormat` 을 새로 만들어서,
 * 스키마 transform 처럼 "행 수 × 날짜 필드 수" 만큼 도는 곳에서는 목록 100행 파싱이
 * 100ms 를 넘겼다. formatToParts 로 연·월·일·시·분·초를 한 번에 꺼낸다.
 */
const KST_FORMATTER = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const KST_AMPM_FORMATTER = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

const KST_KOREAN_SHORT_FORMATTER = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** "2026년 10월 27일 오후 3:42" (KST) */
export const formatKoreanShortKST = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Invalid Date";
  return KST_KOREAN_SHORT_FORMATTER.format(date);
};

type KstParts = Partial<Record<Intl.DateTimeFormatPartTypes, string>>;

const toParts = (formatter: Intl.DateTimeFormat, date: Date): KstParts => {
  const parts: KstParts = {};
  for (const p of formatter.formatToParts(date)) parts[p.type] = p.value;
  return parts;
};

/**
 * ISO 날짜 문자열을 한국 시간 기준으로 포맷팅
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @param options.detail - true일 경우 초(ss)까지 표시 (기본값: false)
 * @returns yyyy-mm-dd hh:mm 또는 yyyy-mm-dd hh:mm:ss 형식의 문자열
 */
export const formatDateKST = (
  dateString: string,
  { detail = false, ampm = false }: { detail?: boolean; ampm?: boolean } = {},
): string => {
  const date = new Date(dateString);
  // 유효하지 않은 Date 는 formatToParts 가 RangeError 를 던진다.
  // 호출부 전체가 죽지 않도록 "Invalid Date" 문자열로 돌려준다.
  if (Number.isNaN(date.getTime())) return "Invalid Date";
  const p = toParts(KST_FORMATTER, date);
  const ymd = `${p.year}-${p.month}-${p.day}`;

  if (ampm) {
    const t = toParts(KST_AMPM_FORMATTER, date);
    const hourVal = (t.hour ?? "00").padStart(2, "0");
    const minuteVal = (t.minute ?? "00").padStart(2, "0");
    return `${ymd} ${t.dayPeriod ?? ""} ${hourVal}:${minuteVal}`;
  }

  const base = `${ymd} ${p.hour}:${p.minute}`;
  return detail ? `${base}:${p.second}` : base;
};

/**
 * `formatDateKST` 가 만든 문자열("yyyy-mm-dd hh:mm[:ss]")을 지금 기준 경과 시간으로 바꾼다.
 * 목록 응답은 스키마 변환에서 이미 이 형식이 되어 원본 ISO 가 남지 않는다.
 * 형식이 어긋나면 null 을 준다.
 */
export const formatElapsedFromKST = (formatted: string): string | null => {
  const match = formatted.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  const at = new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second ?? "00"}+09:00`,
  );
  const diffMinutes = Math.floor((Date.now() - at.getTime()) / 60000);

  if (Number.isNaN(diffMinutes) || diffMinutes < 0) return null;
  if (diffMinutes < 1) return "방금 전";
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}시간 전`;
  return `${Math.floor(diffHours / 24)}일 전`;
};


/**
 * ISO 날짜 문자열을 한국 시간 기준 날짜만 포맷팅
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns yyyy-mm-dd 형식의 문자열
 */
export const formatDateOnlyKST = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Invalid Date";
  const p = toParts(KST_FORMATTER, date);
  return `${p.year}-${p.month}-${p.day}`;
};

/**
 * 날짜 문자열을 Date 로 파싱한다. 값이 없거나 해석되지 않으면 null.
 * 달력·폼이 문자열로 저장된 값을 Date 로 되돌릴 때 쓴다.
 */
export const parseStringToDate = (
  dateStr: string | null | undefined,
): Date | null => {
  if (!dateStr) return null;
  const parsed = new Date(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Date → `yyyy-mm-dd`. 브라우저 로컬 시간대 기준이다 (로컬 = KST 전제).
 *
 * 이미 Date 인 값을 찍는 유일한 구현이다 — 달력·필터·매출 집계가 각자 같은 코드를
 * 갖고 있었다. 문자열(ISO) 을 KST 로 찍는 것은 `formatDateOnlyKST` 다.
 */
export const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// 자정은 "오전 12:00" 이 아니라 "오전 0:00" 으로 찍는다 — 정오는 "오후 12:00" 그대로
const hourLabel = (hour: number, minute: number) =>
  `${hour < 12 ? "오전" : "오후"} ${hour <= 12 ? hour : hour - 12}:${String(minute).padStart(2, "0")}`;

/** 1시간 단위 시각 (`"00:00:00"` ~ `"23:00:00"`) */
export const HOUR_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: `${String(hour).padStart(2, "0")}:00:00`,
  label: hourLabel(hour, 0),
}));

/** `"17:00:00"` → `"오후 5:00"`. `"HH:MM:SS"` 가 아니면 null */
export const formatHour12 = (time?: string | null): string | null => {
  const [h, m] = (time ?? "").split(":");
  const hour = Number(h);
  const minute = Number(m);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  return hourLabel(hour, minute);
};

/** `formatDateOnly` 의 null 통과 판. 값이 비어 있을 수 있는 폼·필터가 쓴다 */
export const formatDateToString = (date: Date | null): string | null =>
  date ? formatDateOnly(date) : null;

/**
 * ISO 날짜 문자열을 한국어 날짜로 포맷 (KST)
 * - 기본: "2026년 1월 22일 14시 29분" — 대응 이력 타임라인용. 월/일/시/분은 0을 채우지 않는다.
 * - `ampm`: "2026년 01월 22일 오후 02:29:35" — 상세 드로어 헤더용. 일·시·분·초를 2자리로 채운다.
 */
export const formatDateKoreanKST = (
  dateString: string,
  { ampm = false }: { ampm?: boolean } = {},
): string => {
  const date = new Date(dateString);
  // formatToParts 는 유효하지 않은 Date 에 RangeError 를 던진다 — 다른 포맷터와 동일하게
  // 예외 대신 "Invalid Date" 문자열로 저하시켜 호출부(타임라인 등) 전체가 죽지 않게 한다.
  if (Number.isNaN(date.getTime())) return "Invalid Date";

  const parts = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: ampm,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  if (ampm) {
    const hour = get("hour").padStart(2, "0");
    return `${get("year")}년 ${get("month")}월 ${get("day")}일 ${get("dayPeriod")} ${hour}:${get("minute")}:${get("second")}`;
  }

  const month = String(Number(get("month")));
  const day = String(Number(get("day")));
  const hour = String(Number(get("hour")));
  const minute = String(Number(get("minute")));

  return `${get("year")}년 ${month}월 ${day}일 ${hour}시 ${minute}분`;
};
